import { Icon } from "components";
import Button from "components/atoms/Button/Button";
import InputField from "components/atoms/InputField/InputField";
import Heading from "components/atoms/layout/Heading/Heading";
import React, { useEffect, useId, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getClientDTO, updateWebhookURL } from "store/slices/client.slice";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { toastHelper } from "utils/helper";
import { errorMessages, infoMessages, successMessages } from "utils/message";
import { cleanObject } from "utils/object";
import { isURL } from "validator";

export const ClientWebhook: React.FC<{ showSmall?: boolean }> = ({
  showSmall,
}) => {
  const dispatch = useAppDispatch();
  const { data: user } = useAppSelector((state) => state.user);
  const { data: client } = useAppSelector((state) => state.client);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const toastId = useId();
  const form = useForm({
    defaultValues: {
      webhookURL: "",
    },
  });
  const sendTrack = (eventEnum: SegmentEventENUM) => {
    const clayPayload = {
      firstname: user.firstName,
      lastname: user.lastName,
      email: user.email,
      webhookUrl: client?.leadWebhookUrl,
    };
    const cleanedFilters = cleanObject(clayPayload);
    window.analytics.track(eventEnum, cleanedFilters);
  };
  const onSave = async (value: { webhookURL: string }) => {
    const isValidURL = validateOnSave(value.webhookURL);
    const isValidateMaxLength = validateMaxLength(value.webhookURL);
    if (!isValidURL) {
      form.setError("webhookURL", { type: "manual", message: "Invalid URL" });
      return;
    }
    if (!isValidateMaxLength) {
      form.setError("webhookURL", {
        type: "manual",
        message:
          "The URL exceeds the maximum permitted length of 500 characters.",
      });
      return;
    }
    setIsSaving(true);
    toastHelper({ message: infoMessages.SAVING_DATA, type: "info", toastId });
    await dispatch(
      updateWebhookURL({
        clientId: user.clientId,
        leadWebhookUrl: value.webhookURL,
      })
    )
      .unwrap()
      .then((data) => {
        if (data.isError) throw new Error(data.message);
        sendTrack(SegmentEventENUM.CLAY_WEBHOOK_SAVED);
        toastHelper({
          message: successMessages.ADD_SUCCESS,
          type: "success",
          toastId,
        });
      })
      .catch((err: any) => {
        toastHelper({
          message: errorMessages.SOMETHING_WENT_WRONG,
          type: "error",
          toastId,
        });
      })
      .finally(() => {
        dispatch(getClientDTO(user.clientId));
        setIsSaving(false);
      });
  };
  const onRemove = async () => {
    setIsRemoving(true);
    toastHelper({ message: infoMessages.DELETING_DATA, type: "info", toastId });
    dispatch(
      updateWebhookURL({ clientId: user.clientId, leadWebhookUrl: null })
    )
      .unwrap()
      .then((data) => {
        if (data.isError) throw new Error(data.message);
        form.reset();
        sendTrack(SegmentEventENUM.CLAY_WEBHOOK_DELETED);
        toastHelper({
          message: successMessages.DELETE_SUCCESS,
          type: "success",
          toastId,
        });
      })
      .catch(() =>
        toastHelper({
          message: errorMessages.SOMETHING_WENT_WRONG,
          type: "error",
          toastId,
        })
      )
      .finally(() => {
        dispatch(getClientDTO(user.clientId));
        setIsRemoving(false);
      });
  };
  const validateMaxLength = (value: string): boolean => {
    if (value.length > 512) {
      form.setError("webhookURL", {
        type: "manual",
        message: "URL is too long",
      });
      return false;
    }
    return true;
  };
  const validateOnSave = (value: string): boolean =>
    isURL(value, { protocols: ["https"], require_valid_protocol: true });
  useEffect(() => {
    if (user.clientId) dispatch(getClientDTO(user.clientId));
  }, [user.clientId]);
  useEffect(() => {
    if (client?.leadWebhookUrl) {
      form.setValue("webhookURL", client?.leadWebhookUrl || "");
    }
  }, [client]);

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSave)}
        className="flex flex-col gap-4"
      >
        {showSmall ? (
          <div className="flex flex-row  gap-4">
            <Icon name="code" size={24} className="text-primary-500" />
            <div className="flex flex-col gap-1">
              <h1 className="font-bold capitalize ">Webhook</h1>
              <small className="text-gray-400">
                Configure a webhook to send deanonymized contact data.
              </small>
            </div>
          </div>
        ) : (
          <Heading
            icon="url"
            iconSize={37}
            title="Webhook"
            description="Configure a webhook to send deanonymized contact data."
          />
        )}
        <Controller
          name="webhookURL"
          render={({ field }) => (
            <InputField
              name="webhookURL"
              size="tiny"
              value={field.value}
              onChange={field.onChange}
              errors={form.formState.errors}
              placeholder="Add your url here..."
            />
          )}
        />
        <div className="flex gap-4">
          <Button
            action="submit"
            iconName="save"
            size="xs"
            disabled={
              form.watch("webhookURL") === client?.leadWebhookUrl ||
              form.watch("webhookURL") === ""
            }
            isProcessing={isSaving}
          >
            Save
          </Button>
          <Button
            action="button"
            size="xs"
            iconName="trash"
            isProcessing={isRemoving}
            iconSize={16}
            disabled={!client?.leadWebhookUrl}
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
